import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import { api, fileUrl } from "@/lib/api";
import { MessageCircle, MapPin, Shield, UserCog } from "lucide-react";
import VerifiedBadge from "@/components/VerifiedBadge";
import SocialIcons from "@/components/SocialIcons";
import ContributorBadge from "@/components/ContributorBadge";

export default function PengurusPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/members", { params: { role: "pengurus" } });
        setItems(data);
      } catch (err) { console.error("Failed to load pengurus:", err); }
      finally { setLoading(false); }
    })();
  }, []);

  const admins = items.filter((m) => m.role === "admin");
  const pengurus = items.filter((m) => m.role === "pengurus");

  return (
    <Layout>
      <div className="mb-8" data-testid="pengurus-header">
        <div className="text-xs uppercase tracking-widest text-bizma-terracotta font-bold flex items-center gap-1"><Shield size={12}/> Struktur Komunitas</div>
        <h1 className="text-3xl sm:text-4xl font-display font-bold mt-1">Pengurus BIZMA</h1>
        <p className="text-bizma-subtext mt-2">Tim inti komunitas — hubungi mereka untuk kolaborasi, event, dan gagasan baru.</p>
      </div>

      {loading ? (
        <div className="text-bizma-subtext">Memuat...</div>
      ) : items.length === 0 ? (
        <div className="felt-card p-10 text-center text-bizma-subtext" data-testid="pengurus-empty">Belum ada pengurus terdaftar.</div>
      ) : (
        <div className="space-y-10">
          {admins.length > 0 && (
            <Section title="Admin" icon={<Shield size={14}/>} tone="terracotta" items={admins} testId="section-admins" />
          )}
          {pengurus.length > 0 && (
            <Section title="Pengurus" icon={<UserCog size={14}/>} tone="sage" items={pengurus} testId="section-pengurus" />
          )}
        </div>
      )}
    </Layout>
  );
}

function Section({ title, icon, tone, items, testId }) {
  const chipCls = tone === "terracotta"
    ? "bg-bizma-terracotta/10 text-bizma-terracotta"
    : "bg-bizma-sage/15 text-bizma-sage";
  return (
    <section data-testid={testId}>
      <div className="flex items-center gap-2 mb-4">
        <span className={`inline-flex items-center gap-1 text-xs uppercase tracking-widest font-bold px-2.5 py-1 rounded-full ${chipCls}`}>{icon} {title}</span>
        <span className="text-sm text-bizma-subtext">{items.length} anggota</span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
        {items.map((m) => <MemberCard key={m.user_id} m={m} />)}
      </div>
    </section>
  );
}

function MemberCard({ m }) {
  return (
    <div className="felt-card overflow-hidden transition-transform hover:-translate-y-1 hover:shadow-md flex flex-col" data-testid={`pengurus-card-${m.user_id}`}>
      <Link to={`/app/members/${m.username || m.user_id}`} className="p-6 flex flex-col items-center text-center flex-1">
        {m.photo_path ? (
          <img src={fileUrl(m.photo_path)} alt={m.name} className="w-24 h-24 rounded-full object-cover ring-4 ring-bizma-terracotta/40" />
        ) : (
          <div className="w-24 h-24 rounded-full bg-bizma-terracotta text-white grid place-items-center text-3xl font-semibold">{m.name?.charAt(0)}</div>
        )}
        <div className="mt-4 font-display font-semibold text-lg flex items-center gap-1 justify-center">
          {m.name}
          {m.is_verified && <VerifiedBadge size={16} />}
        </div>
        {m.username && <div className="text-xs text-bizma-subtext">@{m.username}</div>}
        <div className="mt-1 inline-flex items-center gap-1 text-sm text-bizma-sage font-medium"><MapPin size={12}/> {m.kecamatan || "-"}</div>
        <ContributorBadge active={m.is_top_contributor} className="mt-2" />
        <SocialIcons socials={m.socials} className="mt-3 justify-center" />
      </Link>
      {m.whatsapp && (
        <div className="px-6 pb-6">
          <a href={`https://wa.me/${m.whatsapp.replace(/\D/g, "")}`} target="_blank" rel="noreferrer"
            className="btn-wa text-sm py-2 px-4 w-full" data-testid={`pengurus-wa-${m.user_id}`}>
            <MessageCircle size={16} /> WhatsApp
          </a>
        </div>
      )}
    </div>
  );
}
